import React, { useEffect, useState } from 'react';
import { useMaestroContext } from '../context/MaestroContext';
import maestroService from '../maestroService';
import { formatarData, formatarMoeda } from '../utils/formatters';
import Card from '../components/Card';
import EstadoVazio from '../components/EstadoVazio';
import Header from '../components/Header';
import { SkeletonCard } from '../components/Skeletons';

interface DocumentoFiscal {
  id: number;
  cicloId: number;
  empresaId: number;
  estabelecimento: string;
  tipo: string;
  quantidade: number;
  valor: number;
  responsavelId?: number;
  dataLimite?: string;
  pendencias: string[];
  rejeicoes: string[];
  impactoFiscal?: string;
  impactoFinanceiro?: string;
  impactoContabil?: string;
  status: string;
  criadoEm: string;
}

interface ApuracaoTributaria {
  id: number;
  cicloId: number;
  tributo: string;
  regime: string;
  competencia: string;
  empresaId: number;
  estabelecimento: string;
  baseCalculo: number;
  debito: number;
  credito: number;
  saldoARecolher: number;
  saldoARecuperar: number;
  documentoOrigem?: string;
  statusApuracao: string;
  statusContabilizacao: string;
  statusConciliacao: string;
  responsavelId?: number;
  prazo?: string;
  pendencias: string[];
  alertas: string[];
  impactos?: string;
  criadoEm: string;
}

// Vocabulário real do backend (MaestroDocumentoFiscalService.StatusValidos/TiposValidos e
// MaestroApuracaoTributariaService.StatusValidos/TributosValidos) — confirmado chamada a chamada
// contra a API. Os valores antigos aqui (VALIDADO/REJEITADO/EM_VALIDACAO/NFE/IRPJ/CSLL/DIVERGENTE
// etc.) não existem no backend: qualquer ação que os usasse recebia 400 e falhava silenciosamente.
const STATUS_DOC_BADGE: Record<string, string> = {
  PENDENTE: 'neutro', EMITIDA: 'success', RECUSADA: 'warning', REJEITADA: 'danger', CANCELADA: 'neutro',
};
const STATUS_APURACAO_BADGE: Record<string, string> = {
  PENDENTE: 'neutro', EM_ANDAMENTO: 'warning', CONCLUIDA: 'success',
};

const TIPOS_DOCUMENTO = ['NF_PRODUTO', 'NF_SERVICO', 'NOTA_ENTRADA', 'NOTA_SAIDA', 'NOTA_COMPLEMENTAR', 'NOTA_DEVOLUCAO', 'CANCELAMENTO', 'CARTA_CORRECAO'];
const LABEL_TIPO_DOCUMENTO: Record<string, string> = {
  NF_PRODUTO: 'Nota Fiscal de Produto', NF_SERVICO: 'Nota Fiscal de Serviço', NOTA_ENTRADA: 'Nota de Entrada',
  NOTA_SAIDA: 'Nota de Saída', NOTA_COMPLEMENTAR: 'Nota Complementar', NOTA_DEVOLUCAO: 'Nota de Devolução',
  CANCELAMENTO: 'Cancelamento', CARTA_CORRECAO: 'Carta de Correção',
};
const TRIBUTOS = ['ICMS', 'ICMS_ST', 'IPI', 'ISS', 'PIS', 'COFINS', 'IBS', 'CBS', 'IMPOSTO_SELETIVO'];

// Fluxo Comercial → Faturamento → Fiscal (seção 18 do épico) — documentos fiscais e apurações
// tributárias reais do ciclo ativo, sem nenhum dado fabricado (GET/POST /api/maestro/documentos-fiscais
// e /api/maestro/apuracoes-tributarias).
const MaestroApuracaoTributariaPage: React.FC = () => {
  const { cicloAtivo, empresasEscopo, usuarios, getEmpresa, getUsuario } = useMaestroContext();
  const [aba, setAba] = useState<'DOCUMENTOS' | 'APURACOES'>('DOCUMENTOS');
  const [documentos, setDocumentos] = useState<DocumentoFiscal[]>([]);
  const [apuracoes, setApuracoes] = useState<ApuracaoTributaria[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [mostrarNovoDocumento, setMostrarNovoDocumento] = useState(false);
  const [mostrarNovaApuracao, setMostrarNovaApuracao] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const carregar = () => {
    if (!cicloAtivo.id) { setDocumentos([]); setApuracoes([]); setCarregando(false); return; }
    setCarregando(true);
    Promise.all([
      maestroService.listarDocumentosFiscais(cicloAtivo.id),
      maestroService.listarApuracoesTributarias(cicloAtivo.id),
    ])
      .then(([docs, apur]) => { setDocumentos(docs); setApuracoes(apur); })
      .catch(e => setErro(e?.message ?? 'Não foi possível carregar os dados fiscais do ciclo.'))
      .finally(() => setCarregando(false));
  };

  useEffect(carregar, [cicloAtivo.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [novoDocumento, setNovoDocumento] = useState({
    empresaId: 0, estabelecimento: '', tipo: TIPOS_DOCUMENTO[0], quantidade: 1, valor: 0, responsavelId: 0, dataLimite: '',
  });

  const criarDocumento = () => {
    if (!novoDocumento.empresaId || !novoDocumento.estabelecimento.trim()) return;
    setEnviando(true);
    maestroService.criarDocumentoFiscal({
      cicloId: cicloAtivo.id,
      empresaId: novoDocumento.empresaId,
      estabelecimento: novoDocumento.estabelecimento.trim(),
      tipo: novoDocumento.tipo,
      quantidade: novoDocumento.quantidade,
      valor: novoDocumento.valor,
      responsavelId: novoDocumento.responsavelId || undefined,
      dataLimite: novoDocumento.dataLimite || undefined,
    })
      .then(() => { setMostrarNovoDocumento(false); setNovoDocumento({ empresaId: 0, estabelecimento: '', tipo: TIPOS_DOCUMENTO[0], quantidade: 1, valor: 0, responsavelId: 0, dataLimite: '' }); carregar(); })
      .catch(e => setErro(e?.response?.data?.message ?? 'Não foi possível criar o documento fiscal.'))
      .finally(() => setEnviando(false));
  };

  const validarDocumento = (id: number) =>
    maestroService.atualizarStatusDocumentoFiscal(id, 'EMITIDA').then(carregar).catch(e => setErro(e?.response?.data?.message ?? 'Não foi possível validar o documento.'));
  const rejeitarDocumento = (id: number) => {
    const motivo = window.prompt('Motivo da rejeição:');
    if (!motivo) return;
    maestroService.atualizarStatusDocumentoFiscal(id, 'REJEITADA', [motivo]).then(carregar).catch(e => setErro(e?.response?.data?.message ?? 'Não foi possível rejeitar o documento.'));
  };

  const [novaApuracao, setNovaApuracao] = useState({
    tributo: TRIBUTOS[0], competencia: cicloAtivo.competencia, empresaId: 0, estabelecimento: '',
    baseCalculo: 0, debito: 0, credito: 0, saldoARecolher: 0, saldoARecuperar: 0, responsavelId: 0, prazo: '',
  });

  const criarApuracao = () => {
    if (!novaApuracao.empresaId || !novaApuracao.estabelecimento.trim()) return;
    setEnviando(true);
    maestroService.criarApuracaoTributaria({
      cicloId: cicloAtivo.id,
      tributo: novaApuracao.tributo,
      regime: 'ATUAL',
      competencia: novaApuracao.competencia || cicloAtivo.competencia,
      empresaId: novaApuracao.empresaId,
      estabelecimento: novaApuracao.estabelecimento.trim(),
      baseCalculo: novaApuracao.baseCalculo,
      debito: novaApuracao.debito,
      credito: novaApuracao.credito,
      saldoARecolher: novaApuracao.saldoARecolher,
      saldoARecuperar: novaApuracao.saldoARecuperar,
      responsavelId: novaApuracao.responsavelId || undefined,
      prazo: novaApuracao.prazo || undefined,
    })
      .then(() => { setMostrarNovaApuracao(false); setNovaApuracao({ tributo: TRIBUTOS[0], competencia: cicloAtivo.competencia, empresaId: 0, estabelecimento: '', baseCalculo: 0, debito: 0, credito: 0, saldoARecolher: 0, saldoARecuperar: 0, responsavelId: 0, prazo: '' }); carregar(); })
      .catch(e => setErro(e?.response?.data?.message ?? 'Não foi possível criar a apuração tributária.'))
      .finally(() => setEnviando(false));
  };

  const avancarStatusApuracao = (a: ApuracaoTributaria, campo: 'statusApuracao' | 'statusContabilizacao' | 'statusConciliacao', valor: string) =>
    maestroService.atualizarStatusApuracaoTributaria(a.id, { [campo]: valor }).then(carregar).catch(e => setErro(e?.response?.data?.message ?? 'Não foi possível atualizar o status.'));

  if (carregando) return <div className='mc-page'><Header title='Apuração Tributária' /><SkeletonCard /></div>;

  return (
    <div className='mc-page'>
      <Header
        title='Apuração Tributária e Documentos Fiscais'
        subtitle='Fluxo real Comercial → Faturamento → Fiscal do ciclo ativo — nada aqui é simulado.'
        actions={
          <div className='mc-tabs'>
            <button type='button' className={`mc-btn mc-btn--sm ${aba === 'DOCUMENTOS' ? 'mc-btn--primary' : 'mc-btn--secondary'}`} onClick={() => setAba('DOCUMENTOS')}>Documentos Fiscais</button>
            <button type='button' className={`mc-btn mc-btn--sm ${aba === 'APURACOES' ? 'mc-btn--primary' : 'mc-btn--secondary'}`} onClick={() => setAba('APURACOES')}>Apurações Tributárias</button>
          </div>
        }
      />

      {erro && <p className='mc-texto-danger'>{erro}</p>}
      {!cicloAtivo.id && <EstadoVazio icone='bi bi-calendar-x' titulo='Nenhum ciclo ativo selecionado' descricao='Abra ou selecione um ciclo de fechamento para gerenciar documentos fiscais e apurações.' />}

      {cicloAtivo.id > 0 && aba === 'DOCUMENTOS' && (
        <Card
          title={`${documentos.length} documento(s) fiscal(is)`}
          actions={<button type='button' className='mc-btn mc-btn--primary mc-btn--sm' onClick={() => setMostrarNovoDocumento(v => !v)}>{mostrarNovoDocumento ? 'Cancelar' : 'Novo documento'}</button>}
        >
          {mostrarNovoDocumento && (
            <div className='mc-form-inline'>
              <label>Empresa
                <select value={novoDocumento.empresaId} onChange={e => setNovoDocumento({ ...novoDocumento, empresaId: Number(e.target.value) })}>
                  <option value={0}>Selecione...</option>
                  {empresasEscopo.map(emp => <option key={emp.id} value={emp.id}>{emp.nome}</option>)}
                </select>
              </label>
              <label>Estabelecimento<input type='text' value={novoDocumento.estabelecimento} onChange={e => setNovoDocumento({ ...novoDocumento, estabelecimento: e.target.value })} /></label>
              <label>Tipo
                <select value={novoDocumento.tipo} onChange={e => setNovoDocumento({ ...novoDocumento, tipo: e.target.value })}>
                  {TIPOS_DOCUMENTO.map(t => <option key={t} value={t}>{LABEL_TIPO_DOCUMENTO[t]}</option>)}
                </select>
              </label>
              <label>Quantidade<input type='number' min={1} value={novoDocumento.quantidade} onChange={e => setNovoDocumento({ ...novoDocumento, quantidade: Number(e.target.value) })} /></label>
              <label>Valor<input type='number' min={0} step='0.01' value={novoDocumento.valor} onChange={e => setNovoDocumento({ ...novoDocumento, valor: Number(e.target.value) })} /></label>
              <label>Responsável
                <select value={novoDocumento.responsavelId} onChange={e => setNovoDocumento({ ...novoDocumento, responsavelId: Number(e.target.value) })}>
                  <option value={0}>Sem responsável</option>
                  {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                </select>
              </label>
              <label>Prazo<input type='date' value={novoDocumento.dataLimite} onChange={e => setNovoDocumento({ ...novoDocumento, dataLimite: e.target.value })} /></label>
              <button type='button' className='mc-btn mc-btn--primary' disabled={enviando || !novoDocumento.empresaId || !novoDocumento.estabelecimento.trim()} onClick={criarDocumento}>
                {enviando ? 'Salvando...' : 'Salvar documento'}
              </button>
            </div>
          )}

          {documentos.length === 0
            ? <EstadoVazio titulo='Nenhum documento fiscal registrado neste ciclo' />
            : (
              <table className='mc-logs-table'>
                <thead><tr><th>Empresa</th><th>Estabelecimento</th><th>Tipo</th><th>Qtd</th><th>Valor</th><th>Responsável</th><th>Prazo</th><th>Status</th><th>Ações</th></tr></thead>
                <tbody>
                  {documentos.map(d => (
                    <tr key={d.id}>
                      <td>{getEmpresa(d.empresaId)?.nome ?? `#${d.empresaId}`}</td>
                      <td>{d.estabelecimento}</td>
                      <td>{LABEL_TIPO_DOCUMENTO[d.tipo] ?? d.tipo}</td>
                      <td>{d.quantidade}</td>
                      <td>{formatarMoeda(d.valor)}</td>
                      <td>{getUsuario(d.responsavelId)?.nome ?? '—'}</td>
                      <td>{d.dataLimite ? formatarData(d.dataLimite.slice(0, 10)) : '—'}</td>
                      <td><span className={`mc-badge mc-badge--${STATUS_DOC_BADGE[d.status] ?? 'neutro'}`}>{d.status}</span></td>
                      <td>
                        {d.status === 'PENDENTE' && (
                          <>
                            <button type='button' className='mc-btn mc-btn--sm mc-btn--secondary' onClick={() => validarDocumento(d.id)}>Validar</button>{' '}
                            <button type='button' className='mc-btn mc-btn--sm mc-btn--secondary' onClick={() => rejeitarDocumento(d.id)}>Rejeitar</button>
                          </>
                        )}
                        {d.rejeicoes.length > 0 && <small className='mc-texto-danger'> {d.rejeicoes[d.rejeicoes.length - 1]}</small>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </Card>
      )}

      {cicloAtivo.id > 0 && aba === 'APURACOES' && (
        <Card
          title={`${apuracoes.length} apuração(ões) tributária(s)`}
          actions={<button type='button' className='mc-btn mc-btn--primary mc-btn--sm' onClick={() => setMostrarNovaApuracao(v => !v)}>{mostrarNovaApuracao ? 'Cancelar' : 'Nova apuração'}</button>}
        >
          {mostrarNovaApuracao && (
            <div className='mc-form-inline'>
              <label>Tributo
                <select value={novaApuracao.tributo} onChange={e => setNovaApuracao({ ...novaApuracao, tributo: e.target.value })}>
                  {TRIBUTOS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label>Empresa
                <select value={novaApuracao.empresaId} onChange={e => setNovaApuracao({ ...novaApuracao, empresaId: Number(e.target.value) })}>
                  <option value={0}>Selecione...</option>
                  {empresasEscopo.map(emp => <option key={emp.id} value={emp.id}>{emp.nome}</option>)}
                </select>
              </label>
              <label>Estabelecimento<input type='text' value={novaApuracao.estabelecimento} onChange={e => setNovaApuracao({ ...novaApuracao, estabelecimento: e.target.value })} /></label>
              <label>Competência<input type='text' placeholder='MM/yyyy' value={novaApuracao.competencia} onChange={e => setNovaApuracao({ ...novaApuracao, competencia: e.target.value })} /></label>
              <label>Base de cálculo<input type='number' min={0} step='0.01' value={novaApuracao.baseCalculo} onChange={e => setNovaApuracao({ ...novaApuracao, baseCalculo: Number(e.target.value) })} /></label>
              <label>Débito<input type='number' min={0} step='0.01' value={novaApuracao.debito} onChange={e => setNovaApuracao({ ...novaApuracao, debito: Number(e.target.value) })} /></label>
              <label>Crédito<input type='number' min={0} step='0.01' value={novaApuracao.credito} onChange={e => setNovaApuracao({ ...novaApuracao, credito: Number(e.target.value) })} /></label>
              <label>Saldo a recolher<input type='number' min={0} step='0.01' value={novaApuracao.saldoARecolher} onChange={e => setNovaApuracao({ ...novaApuracao, saldoARecolher: Number(e.target.value) })} /></label>
              <label>Saldo a recuperar<input type='number' min={0} step='0.01' value={novaApuracao.saldoARecuperar} onChange={e => setNovaApuracao({ ...novaApuracao, saldoARecuperar: Number(e.target.value) })} /></label>
              <label>Responsável
                <select value={novaApuracao.responsavelId} onChange={e => setNovaApuracao({ ...novaApuracao, responsavelId: Number(e.target.value) })}>
                  <option value={0}>Sem responsável</option>
                  {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                </select>
              </label>
              <label>Prazo<input type='date' value={novaApuracao.prazo} onChange={e => setNovaApuracao({ ...novaApuracao, prazo: e.target.value })} /></label>
              <button type='button' className='mc-btn mc-btn--primary' disabled={enviando || !novaApuracao.empresaId || !novaApuracao.estabelecimento.trim()} onClick={criarApuracao}>
                {enviando ? 'Salvando...' : 'Salvar apuração'}
              </button>
            </div>
          )}

          {apuracoes.length === 0
            ? <EstadoVazio titulo='Nenhuma apuração tributária registrada neste ciclo' />
            : (
              <table className='mc-logs-table'>
                <thead><tr><th>Tributo</th><th>Empresa</th><th>Competência</th><th>Base</th><th>A recolher</th><th>A recuperar</th><th>Apuração</th><th>Contabilização</th><th>Conciliação</th></tr></thead>
                <tbody>
                  {apuracoes.map(a => (
                    <tr key={a.id}>
                      <td>{a.tributo}</td>
                      <td>{getEmpresa(a.empresaId)?.nome ?? `#${a.empresaId}`}</td>
                      <td>{a.competencia}</td>
                      <td>{formatarMoeda(a.baseCalculo)}</td>
                      <td>{formatarMoeda(a.saldoARecolher)}</td>
                      <td>{formatarMoeda(a.saldoARecuperar)}</td>
                      <td>
                        <select className={`mc-badge mc-badge--${STATUS_APURACAO_BADGE[a.statusApuracao] ?? 'neutro'}`} value={a.statusApuracao} onChange={e => avancarStatusApuracao(a, 'statusApuracao', e.target.value)}>
                          <option value='PENDENTE'>PENDENTE</option>
                          <option value='EM_ANDAMENTO'>EM_ANDAMENTO</option>
                          <option value='CONCLUIDA'>CONCLUIDA</option>
                        </select>
                      </td>
                      <td>
                        <select className={`mc-badge mc-badge--${STATUS_APURACAO_BADGE[a.statusContabilizacao] ?? 'neutro'}`} value={a.statusContabilizacao} onChange={e => avancarStatusApuracao(a, 'statusContabilizacao', e.target.value)}>
                          <option value='PENDENTE'>PENDENTE</option>
                          <option value='EM_ANDAMENTO'>EM_ANDAMENTO</option>
                          <option value='CONCLUIDA'>CONCLUIDA</option>
                        </select>
                      </td>
                      <td>
                        <select className={`mc-badge mc-badge--${STATUS_APURACAO_BADGE[a.statusConciliacao] ?? 'neutro'}`} value={a.statusConciliacao} onChange={e => avancarStatusApuracao(a, 'statusConciliacao', e.target.value)}>
                          <option value='PENDENTE'>PENDENTE</option>
                          <option value='EM_ANDAMENTO'>EM_ANDAMENTO</option>
                          <option value='CONCLUIDA'>CONCLUIDA</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </Card>
      )}
    </div>
  );
};

export default MaestroApuracaoTributariaPage;
